import _ from "underscore";
import { BaseCache, makeDeviceCache } from "../../utils/storage/cache";
import * as logger from "../../utils/logger";
import Api from "../../services/api";
import { extend } from "../../utils/vanilla";
import Spinner from "../../utils/spinner";
import { MobileActions } from "../../store/mobile/actions";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { IUnassignmentProximityInfo, IAssignmentProximityInfo } from "../../services/vehicleProximity";
import { getCurrentVehicle } from "../../utils/device";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import pageHTML from "./vehicleProximity.tmpl";
import assignmentDataHTML from "./vehicleProximity.data.tmpl";
import unassignmentDataHTML from "./vehicleProximity.unassignmentData.tmpl";
import { Device } from "../../model/Device";
import DebugPageView from "./debugPageView";

    export default class VehicleProximityView extends DebugPageView {
        private readonly api: Api;
        private readonly deviceCache: BaseCache<Device | IDevice>;
        private readonly template = _.template(pageHTML);
        private readonly assignmentDataTemplate = _.template(assignmentDataHTML);
        private readonly unassignmentDataTemplate = _.template(unassignmentDataHTML);
        private readonly guesstimates = 1;
        private readonly initDisabledAutoDrive = window.DRIVE_DEBUG && window.DRIVE_DEBUG.DISABLE_AUTO_DRIVE_ACTIONS;
        private readonly spinner = new Spinner();
        private checkIntervalSec = 10;
        private checkTimeout: number | undefined;
        private triggerUnassignTimeout: number | undefined;
        private getDataPromise: Promise;
        private getUnassignmentDataPromise: Promise;

        constructor(options) {
            super(options);
            this.api = this.app.api;
            this.deviceCache = makeDeviceCache(this.app.store);

            if (window.DRIVE_DEBUG) {
                window.DRIVE_DEBUG.DISABLE_AUTO_DRIVE_ACTIONS = true;
            }
        }

        /* eslint-disable @typescript-eslint/unbound-method */
        events() {
            return {
                "keyup #debug-vehicleproximity-interval": this.updateInterval,
                "click #debug-vehicleproximity-triggerUnassignment": this.triggerUnassignment
            };
        }
        /* eslint-enable @typescript-eslint/unbound-method */

        render() {
            this.el.innerHTML = this.template({
                intervalSec: this.checkIntervalSec
            });

            this.app.store.dispatch(MobileActions.setPageState({
                title: "Vehicle Proximity",
                pageLevel: 2
            }));

            let debugDataWrapper = <HTMLDivElement>document.getElementById("debug-vehicleproximity-data");
            this.spinner.spin(debugDataWrapper);

            this.update(0);
            return this;
        }

        renderAssignmentData(proximityData?: IAssignmentProximityInfo) {
            let renderWithCloseDevices = (closeDevices: any) => {
                this.spinner.stop();
                let debugDataWrapper = <HTMLDivElement>document.getElementById("debug-vehicleproximity-data");
                debugDataWrapper.innerHTML = this.assignmentDataTemplate(extend(proximityData, {
                    closeDevices
                }));
            };

            if (proximityData && proximityData.closeStatuses) {
                let lookupDevices = proximityData.closeStatuses.map(status => status.device);
                if (lookupDevices && lookupDevices.length) {
                    this.deviceCache.getOrFetch(this.api, lookupDevices)
                        .then((result: any[]) => {
                            let closeDevices = result.reduce((acc, items) => acc.concat(items), []);
                            let closestDevices = closeDevices && closeDevices.length > this.guesstimates ? closeDevices.slice(0, this.guesstimates) : closeDevices;
                            renderWithCloseDevices(closestDevices);
                        })
                        .catch(error => logger.error(logger.Source.API, `Error when getOrFetch devices : '${ error.message }'`, this.api.getUser(), error));
                } else {
                    renderWithCloseDevices(null);
                }
            }
        }

        renderUnassignmentData(unassignmentData?: IUnassignmentProximityInfo) {
            this.spinner.stop();
            let debugDataWrapper = <HTMLDivElement>document.getElementById("debug-vehicleproximity-unassignmentData");
            let currentDevice = getCurrentVehicle();
            let render = (device?) => {
                debugDataWrapper.innerHTML = this.unassignmentDataTemplate(extend(unassignmentData || {}, {
                    deviceName: device ? device.name : "Unknown"
                }));
            };
            if (currentDevice) {
                void this.deviceCache.getOrFetch(this.api, currentDevice).then(devices => {
                    render(devices && devices[0] && devices[0][0]);
                });
            } else {
                render();
            }
        }

        update(checkInterval?: number) {
            this.checkTimeout = setTimeout(() => {
                let vehicleProximity = this.app.context.vehicleProximity;
                this.getDataPromise = vehicleProximity.getAssignmentInfo(undefined, false).then(this.renderAssignmentData.bind(this), () => this.renderAssignmentData());
                this.getUnassignmentDataPromise = vehicleProximity.getUnassignmentStatus().then(this.renderUnassignmentData.bind(this), () => this.renderUnassignmentData());
                this.update();
            }, typeof (checkInterval) !== "undefined" ? checkInterval : this.checkIntervalSec * 1000);
        }

        updateInterval(e: Event) {
            let inputInterval = (e.target as HTMLInputElement)?.value;
            if (!inputInterval) {
                return;
            }
            this.checkIntervalSec = parseInt(inputInterval, 10);

            // restart watcher
            window.clearTimeout(this.checkTimeout);
            this.checkTimeout = undefined;
            this.update(0);
        }

        triggerUnassignment() {
            window.clearTimeout(this.triggerUnassignTimeout);
            this.triggerUnassignTimeout = setTimeout(() => {
                let apiUser = this.api.getUser();
                dispatcher.trigger(DispatcherEvents.VehicleOutOfRange, apiUser?.name || "");
            }, 3 * 1000);
        }

        remove() {
            window.clearTimeout(this.checkTimeout);
            this.checkTimeout = undefined;
            this.getDataPromise && this.getDataPromise.cancel();
            this.getUnassignmentDataPromise && this.getUnassignmentDataPromise.cancel();

            if (window.DRIVE_DEBUG) {
                window.DRIVE_DEBUG.DISABLE_AUTO_DRIVE_ACTIONS = !!this.initDisabledAutoDrive;
            }
            super.remove();
            return this;
        }
    }